// src/components/RhinoVOIPVoicemails.jsx
import React, { useState, useEffect } from 'react';
import { useGetVoicemailsByExtensionQuery } from '../services/VoipService';
import useIsMobile from '../hooks/useIsMobile';
import '../assets/styles/rhinovoip-voicemails.less';
import { useVoip } from '../voip/VoipProvider';
import ringGroupIcon from '../assets/images/voip/ring-group-icon.svg';
import RhinoVoipVoicemailPlayer from './RhinoVoipVoicemailPlayer';

// ------------------ Component ------------------

const RhinoVOIPVoicemails = ({ autoPlayVoicemailId, onAutoPlayConsumed }) => {
  const { myExtension, call: placeCall, callPhase } = useVoip();
  const [includeRinggroups, setIncludeRinggroups] = useState(true);

  const {
    data,
    isLoading,
    error,
    refetch,
  } = useGetVoicemailsByExtensionQuery();

  // Refetch on mount and then every 10 seconds
  useEffect(() => {
    refetch(); // on component load

    const intervalId = setInterval(() => {
      refetch();
    }, 10_000);

    return () => {
      clearInterval(intervalId);
    };
  }, [refetch]);

  // Keep your existing "call ended" refresh (slight delay)
  useEffect(() => {
    let t;

    if (callPhase === 'ended') {
      t = setTimeout(() => {
        refetch();
      }, 500);
    }

    return () => {
      if (t) clearTimeout(t);
    };
  }, [callPhase, refetch]);

  const [pendingAutoPlay, setPendingAutoPlay] = useState(null);

  useEffect(() => {
    if (autoPlayVoicemailId) {
      setPendingAutoPlay(autoPlayVoicemailId);
      onAutoPlayConsumed();
    }
  }, [autoPlayVoicemailId, onAutoPlayConsumed]);

  const [expandedRow, setExpandedRow] = useState(null);
  const [confirmCall, setConfirmCall] = useState(null);
  const isMobile = useIsMobile(768);

  if (isLoading) return <div>Loading call logs...</div>;
  if (error) return <div>Error loading call logs</div>;

  const calls = Array.isArray(data?.objects) ? data.objects : [];

  function extractCallTarget(call) {
    return call.caller_id_number;
  }

  function handleMobileClick(call) {
    const target = extractCallTarget(call);
    if (!target) return;

    try {
      placeCall(target);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Mobile call start failed:', err);
    }
  }

  function formatEpochDate(epoch) {
    if (epoch == null || epoch === '') return '-';

    const n = Number(epoch);
    if (!Number.isFinite(n)) return '-';

    // support epoch in seconds (10 digits) or milliseconds (13 digits)
    const ms = n < 1e12 ? n * 1000 : n;

    const d = new Date(ms);
    if (Number.isNaN(d.getTime())) return '-';

    return `${d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    })} ${d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    })}`;
  }

  function formatPhone(value) {
    if (!value) return value;

    const digits = value.replace(/\D/g, '');

    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }

    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }

    return value;
  }

  function formatDuration(sec) {
    const n = Number(sec);
    if (!Number.isFinite(n) || n <= 0) return '00:00';

    const total = Math.floor(n);
    const minutes = Math.floor(total / 60);
    const seconds = total % 60;

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }

  function displayWithRingGroupIcon(label, number = null, showIcon = true) {
    const text = number ? `${label} (${number})` : label;

    if (!showIcon) return text;

    return (
      <span className="ringgroup-label">
        <img src={ringGroupIcon} alt="Ring Group" className="ringgroup-icon" /> {text}
      </span>
    );
  }

  function truncateTranscription(text, maxLen) {
    if (!text) return 'No Transcription Available';
    if (text.length <= maxLen) return text;

    return `${text.slice(0, maxLen)}...`;
  }

  // ------------------ Caller / Receiver Logic ------------------

  function callFrom(call) {
    const num = call.caller_id_number;
    const name = call.caller_id_name || num || 'Unknown';

    if (num === myExtension) return `Me (${num})`;

    if (name === num) {
      return formatPhone(num);
    } else {
      return formatPhone(`${name} (${num})`);
    }
  }

  function displayParty(call) {
    return callFrom(call);
  }

  function displayCallTarget(call) {
    const rgName = call.ring_group_name;

    if (rgName) {
      return displayWithRingGroupIcon(rgName, call.ring_group_extension, true);
    }

    if (isMobile) {
      return '';
    }

    return 'Direct';
  }

  function getCallConfirmDialogtext() {
    const { label, targetNumber } = confirmCall;

    if (label === targetNumber) {
      return `Call ${formatPhone(targetNumber)}?`;
    } else {
      return `Call ${label} at ${formatPhone(targetNumber)}?`;
    }
  }

  // function markVmAs(voicemailMessageUuid, status) {

  // }

  // function downloadVm(voicemailMessageUuid) {

  // }

  return (
    <div className="vm-container">
      {/* Checkbox */}
      <label
        style={{
          display: 'flex',
          gap: '6px',
          alignItems: 'center',
          marginBottom: '8px',
        }}
      >
        <input
          type="checkbox"
          checked={includeRinggroups}
          onChange={(e) => setIncludeRinggroups(e.target.checked)}
        />
        Include Ring Group Calls
      </label>

      {isMobile ? (
        // -------- MOBILE VIEW --------
        <div className="vm-mobile-list">
          {calls.map((call) => {
            const isExpanded = expandedRow === call.voicemail_message_uuid;
            const transcription = call.message_transcription;

            return (
              <div
                key={call.voicemail_message_uuid}
                className={`vm-mobile-card${isExpanded ? ' vm-mobile-expanded' : ''}`}
                onClick={() => setExpandedRow(isExpanded ? null : call.voicemail_message_uuid)}
              >
                <div className="vm-header-row">
                  <span className="vm-name">
                    &nbsp;&nbsp;{displayParty(call)}
                    <br />
                    {displayCallTarget(call)}
                  </span>

                  <span className="vm-ts">
                    {formatEpochDate(call.created_epoch)}
                  </span>
                </div>

                <div className="vm-meta">
                  {formatDuration(call.message_length)}
                  <span className="vm-transcription-preview" style={{ marginLeft: '8px' }}>
                    {truncateTranscription(transcription, 40)}
                  </span>
                </div>

                {isExpanded && (
                  <div className="vm-transcription-full" style={{ marginTop: '8px' }}>
                    {transcription || 'No Transcription Available'}
                    <div style={{ marginTop: '8px' }}>
                      <RhinoVoipVoicemailPlayer
                        voicemailMessageId={call.voicemail_message_uuid}
                        autoPlay={call.voicemail_message_uuid === pendingAutoPlay}
                        onAutoPlayStarted={() => setPendingAutoPlay(null)}
                      />
                    </div>
                    <button
                      type="button"
                      className="vm-mobile-callback-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMobileClick(call);
                      }}
                    >
                      Call Back
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        // -------- DESKTOP TABLE VIEW --------
        <div className="vm-table-container">
          <table className="vm-table">
            <thead>
              <tr>
                <th>Party</th>
                <th>Direct/Group</th>
                <th>Audio</th>
                <th>Received</th>
                <th>Duration</th>
                {/* <th style={{ width: 44 }}> </th> */}
              </tr>
            </thead>

            <tbody>
              {calls.map((call) => {
                const isExpanded = expandedRow === call.voicemail_message_uuid;
                const transcription = call.message_transcription;

                return (
                  <React.Fragment key={call.voicemail_message_uuid}>
                    <tr
                      className={`vm-row-clickable${isExpanded ? ' vm-row-expanded' : ''}`}
                      onClick={() => setExpandedRow(isExpanded ? null : call.voicemail_message_uuid)}
                    >
                      <td>
                        <div>{displayParty(call)}</div>
                        <div className="vm-transcription-preview">
                          {truncateTranscription(transcription, 60)}
                        </div>
                      </td>
                      <td>{displayCallTarget(call)}</td>
                      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
                      <td onClick={(e) => e.stopPropagation()} role="cell">
                        <RhinoVoipVoicemailPlayer
                          voicemailMessageId={call.voicemail_message_uuid}
                          autoPlay={call.voicemail_message_uuid === pendingAutoPlay}
                          onAutoPlayStarted={() => setPendingAutoPlay(null)}
                        />
                      </td>
                      <td>{formatEpochDate(call.created_epoch)}</td>
                      <td>{formatDuration(call.message_length)}</td>
                    </tr>
                    {isExpanded && (
                      <tr className="vm-transcription-row">
                        <td colSpan={5}>
                          <div className="vm-transcription-full">
                            {transcription || 'No Transcription Available'}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {confirmCall && (
        <div className="vm-modal-overlay">
          <div className="vm-modal">
            <div className="vm-modal-title">Confirm Call</div>
            <div className="vm-modal-body">
              {getCallConfirmDialogtext(confirmCall)}
            </div>

            <div className="vm-modal-actions">
              <button
                type="button"
                className="vm-modal-cancel"
                onClick={() => setConfirmCall(null)}
              >
                Cancel
              </button>

              <button
                type="button"
                className="vm-modal-confirm"
                onClick={() => {
                  placeCall(confirmCall.targetNumber);
                  setConfirmCall(null);
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RhinoVOIPVoicemails;
